import { useState } from "react"
import { XClose } from "@untitledui/icons"
import { AInput, EMAIL_RE, Field, SelectTile } from "@/components/onboarding-activation/ui"
import type { IntakeConfig, ShopBranding } from "@/lib/intake/intake-config"
import { withShopName } from "@/lib/intake/intake-config"
import { normalizePhone } from "@/lib/utils/phone"
import { cx } from "@/lib/utils/cx"

// "Set up" tab — everything the shop can change about the consumer flow.
// Controlled: the parent owns the form state, dirty tracking, and the save
// bar; this panel only renders + patches. Rule-divided sections in ONE white
// container (brand rule — never stacked white boxes). The parent wraps the
// whole tab in <fieldset disabled> for non-managers.

interface SetupPanelProps {
  intake: IntakeConfig
  branding: ShopBranding
  shopName: string
  onIntake: (patch: Partial<IntakeConfig>) => void
  onBranding: (patch: Partial<ShopBranding>) => void
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/

const EXTRAS: { key: keyof IntakeConfig["extras"]; title: string; sub: string }[] = [
  { key: "vehicle", title: "Which car it is", sub: "VIN photo, plate, or year/make/model" },
  { key: "situation", title: "What happened", sub: "Drivable, cause, a note" },
  { key: "insurance", title: "Insurance or out-of-pocket", sub: "Carrier and claim number" },
  { key: "booking", title: "Offer a visit time", sub: "Books straight into your calendar" },
  { key: "uploadLater", title: "Photos later by text", sub: "We text a link to add photos" },
]

const REQUIRED: { key: keyof IntakeConfig["required"]; title: string; sub: string }[] = [
  { key: "email", title: "Email address", sub: "Off = phone alone is enough" },
  { key: "vehicle", title: "Vehicle identity", sub: "Customer can't skip the car step" },
]

export function SetupPanel({ intake, branding, shopName, onIntake, onBranding }: SetupPanelProps) {
  return (
    <div className="divide-y divide-zinc-100 rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
      {/* Status */}
      <section className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[13px] font-semibold text-zinc-800">Taking online requests</p>
            <p className="mt-1 max-w-md text-[12.5px] leading-snug text-zinc-500">
              Your link, QR code, website form, and kiosk all run off this one
              switch. Paused means visitors see &ldquo;call us&rdquo; instead of the
              photo flow.
            </p>
          </div>
          <Switch
            on={intake.enabled}
            label="Taking online requests"
            onChange={(v) => onIntake({ enabled: v })}
          />
        </div>
        {!intake.enabled && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-[12.5px] leading-snug text-amber-900">
            Consumer intake is paused — no new requests can come in until you
            turn it back on.
          </p>
        )}
      </section>

      {/* Welcome */}
      <section className="p-5 md:p-6">
        <SectionLabel>Welcome screen</SectionLabel>
        <div className="mt-3 grid gap-4">
          <Field label="Title">
            <AInput
              value={intake.welcome.title}
              onChange={(e) =>
                onIntake({ welcome: { ...intake.welcome, title: e.target.value } })
              }
              placeholder="Let's see the damage."
            />
          </Field>
          <Field label="Message" hint="{shop} becomes your shop name">
            <textarea
              value={intake.welcome.message}
              onChange={(e) =>
                onIntake({ welcome: { ...intake.welcome, message: e.target.value } })
              }
              rows={2}
              className={textareaClass}
            />
          </Field>
          <p className="text-[12px] leading-snug text-zinc-400">
            Shows as: &ldquo;{withShopName(intake.welcome.message, shopName)}&rdquo;
          </p>
        </div>
      </section>

      {/* Brand */}
      <section className="p-5 md:p-6">
        <SectionLabel>Brand</SectionLabel>
        <p className="mt-1 text-[12.5px] leading-snug text-zinc-500">
          A logo chip and an accent color — the flow itself stays clean.
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)]">
          {/* keyed so a new URL retries the image after a previous 404 */}
          <BrandChip key={branding.logoUrl ?? "initials"} branding={branding} />
          <div className="grid gap-4">
            <Field label="Logo URL" hint="falls back to initials">
              <AInput
                value={branding.logoUrl ?? ""}
                onChange={(e) => onBranding({ logoUrl: e.target.value || null })}
                placeholder="https://yourshop.com/logo.png"
                inputMode="url"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Initials">
                <AInput
                  value={branding.logoInitials ?? ""}
                  onChange={(e) =>
                    onBranding({ logoInitials: e.target.value.slice(0, 3) || null })
                  }
                  placeholder="LC"
                />
              </Field>
              <Field label="Accent color">
                <div className="relative">
                  <AInput
                    value={branding.primaryColor}
                    valid={HEX_RE.test(branding.primaryColor)}
                    invalid={branding.primaryColor.length > 0 && !HEX_RE.test(branding.primaryColor)}
                    onChange={(e) => onBranding({ primaryColor: e.target.value })}
                    placeholder="#059669"
                    className="pl-9"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 rounded border border-zinc-200"
                    style={{
                      background: HEX_RE.test(branding.primaryColor)
                        ? branding.primaryColor
                        : "#E4E4E7",
                    }}
                  />
                </div>
              </Field>
            </div>
            <Field label="Hours" hint="printed on the counter card">
              <AInput
                value={branding.hours}
                onChange={(e) => onBranding({ hours: e.target.value })}
                placeholder="Mon–Fri 8AM–6PM"
              />
            </Field>
          </div>
        </div>
      </section>

      {/* What you ask for */}
      <section className="p-5 md:p-6">
        <SectionLabel>What you ask for</SectionLabel>
        <p className="mt-1 text-[12.5px] leading-snug text-zinc-500">
          Name, phone, and photos are always part of the flow. Everything below
          is optional for the customer — turn off what you don&rsquo;t need.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {EXTRAS.map(({ key, title, sub }) => (
            <ExtraTile
              key={key}
              on={intake.extras[key]}
              title={title}
              sub={sub}
              onToggle={() =>
                onIntake({ extras: { ...intake.extras, [key]: !intake.extras[key] } })
              }
            />
          ))}
        </div>

        <p className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-400">
          Required to continue
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {REQUIRED.map(({ key, title, sub }) => (
            <ExtraTile
              key={key}
              on={intake.required[key]}
              title={title}
              sub={sub}
              onToggle={() =>
                onIntake({ required: { ...intake.required, [key]: !intake.required[key] } })
              }
            />
          ))}
        </div>
      </section>

      {/* Consent */}
      <section className="p-5 md:p-6">
        <SectionLabel>Consent</SectionLabel>
        <div className="mt-3 grid gap-3">
          <Field label="Consent line" hint="shown under the contact form">
            <textarea
              value={intake.consent.text}
              onChange={(e) =>
                onIntake({ consent: { ...intake.consent, text: e.target.value } })
              }
              rows={3}
              className={textareaClass}
            />
          </Field>
          <div className="flex items-start justify-between gap-4">
            <p className="max-w-md text-[12.5px] leading-snug text-zinc-500">
              Start the &ldquo;text me updates&rdquo; toggle checked for the customer.
              They can always turn it off.
            </p>
            <Switch
              on={intake.consent.smsOptIn}
              label="SMS opt-in checked by default"
              onChange={(v) => onIntake({ consent: { ...intake.consent, smsOptIn: v } })}
            />
          </div>
        </div>
      </section>

      {/* Notify */}
      <section className="p-5 md:p-6">
        <SectionLabel>Who hears about new leads</SectionLabel>
        <p className="mt-1 text-[12.5px] leading-snug text-zinc-500">
          Saved now — email and text alerts arrive with go-live.
        </p>
        <div className="mt-3 grid gap-4">
          <ChipListInput
            label="Emails"
            placeholder="frontdesk@yourshop.com"
            values={intake.notify.emails}
            validate={(v) => EMAIL_RE.test(v)}
            normalize={(v) => v.trim().toLowerCase()}
            onChange={(emails) => onIntake({ notify: { ...intake.notify, emails } })}
          />
          <ChipListInput
            label="Phones"
            placeholder="(555) 123-4567"
            values={intake.notify.phones}
            validate={(v) => normalizePhone(v).valid}
            normalize={(v) => normalizePhone(v).display}
            onChange={(phones) => onIntake({ notify: { ...intake.notify, phones } })}
          />
          <div className="flex flex-wrap gap-2">
            <NotifyChip
              on={intake.notify.onNewLead}
              label="New lead"
              onClick={() =>
                onIntake({ notify: { ...intake.notify, onNewLead: !intake.notify.onNewLead } })
              }
            />
            <NotifyChip
              on={intake.notify.onBooking}
              label="Booking made"
              onClick={() =>
                onIntake({ notify: { ...intake.notify, onBooking: !intake.notify.onBooking } })
              }
            />
          </div>
        </div>
      </section>

      {/* Kiosk timers */}
      <section className="p-5 md:p-6">
        <SectionLabel>Kiosk timers</SectionLabel>
        <p className="mt-1 text-[12.5px] leading-snug text-zinc-500">
          The lobby tablet resets itself so the next customer never sees the
          last one&rsquo;s request.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-4 max-w-sm">
          <Field label="After done, reset in">
            <SecondsSelect
              value={intake.kiosk.doneResetSeconds}
              options={[10, 15, 20, 30, 60]}
              label="Done-screen reset"
              onChange={(v) => onIntake({ kiosk: { ...intake.kiosk, doneResetSeconds: v } })}
            />
          </Field>
          <Field label="If idle, reset in">
            <SecondsSelect
              value={intake.kiosk.idleResetSeconds}
              options={[60, 90, 120, 180, 300]}
              label="Idle reset"
              onChange={(v) => onIntake({ kiosk: { ...intake.kiosk, idleResetSeconds: v } })}
            />
          </Field>
        </div>
      </section>
    </div>
  )
}

const textareaClass =
  "w-full rounded-lg border border-[#D5DDD8] bg-white px-3.5 py-2.5 text-[14.5px] leading-relaxed text-[#0C2018] placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
      {children}
    </p>
  )
}

function BrandChip({ branding }: { branding: ShopBranding }) {
  const [broken, setBroken] = useState(false)
  const showLogo = !!branding.logoUrl && !broken
  return (
    <div
      className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-2xl text-[18px] font-bold text-white shadow-md"
      style={{ background: HEX_RE.test(branding.primaryColor) ? branding.primaryColor : "#059669" }}
      aria-hidden
    >
      {showLogo ? (
        <img
          src={branding.logoUrl ?? undefined}
          alt=""
          onError={() => setBroken(true)}
          onLoad={() => setBroken(false)}
          className="size-full object-cover"
        />
      ) : (
        (branding.logoInitials || "?").slice(0, 2).toUpperCase()
      )}
    </div>
  )
}

function ExtraTile({
  on,
  title,
  sub,
  onToggle,
}: {
  on: boolean
  title: string
  sub: string
  onToggle: () => void
}) {
  return (
    <SelectTile selected={on} onClick={onToggle} role="checkbox">
      <span className="min-w-0">
        <span className="block truncate">{title}</span>
        <span className="mt-0.5 block text-[12px] font-normal leading-snug text-zinc-400">
          {sub}
        </span>
      </span>
    </SelectTile>
  )
}

function NotifyChip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      className={cx(
        "h-9 rounded-lg px-3.5 text-[12.5px] font-semibold transition-colors",
        on
          ? "bg-emerald-600 text-white"
          : "border border-zinc-200 bg-white text-zinc-400 hover:text-zinc-700",
      )}
    >
      {label}
    </button>
  )
}

// Add/remove chip list with validation — notify emails + phones.
function ChipListInput({
  label,
  placeholder,
  values,
  validate,
  normalize,
  onChange,
}: {
  label: string
  placeholder: string
  values: string[]
  validate: (v: string) => boolean
  normalize: (v: string) => string
  onChange: (values: string[]) => void
}) {
  const [draft, setDraft] = useState("")
  const normalized = normalize(draft)
  const canAdd = draft.trim().length > 0 && validate(draft) && !values.includes(normalized)

  const add = () => {
    if (!canAdd) return
    onChange([...values, normalized])
    setDraft("")
  }

  return (
    <div>
      <p className="mb-1.5 text-[13px] font-semibold text-[#33413A]">{label}</p>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <AInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                add()
              }
            }}
            placeholder={placeholder}
            aria-label={`Add ${label.toLowerCase()}`}
          />
        </div>
        <button
          type="button"
          disabled={!canAdd}
          onClick={add}
          className="h-11 shrink-0 rounded-lg bg-zinc-900 px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-40"
        >
          Add
        </button>
      </div>
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 px-2 py-1 text-[12px] font-medium text-zinc-700"
            >
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="text-zinc-400 hover:text-zinc-700"
              >
                <XClose className="size-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function SecondsSelect({
  value,
  options,
  label,
  onChange,
}: {
  value: number
  options: number[]
  label: string
  onChange: (v: number) => void
}) {
  // A saved custom value that isn't in the preset list still has to render.
  const opts = options.includes(value) ? options : [...options, value].sort((a, b) => a - b)
  return (
    <select
      value={value}
      aria-label={label}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-10 w-full rounded-lg border border-zinc-200 bg-white px-2.5 text-[13px] text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
    >
      {opts.map((s) => (
        <option key={s} value={s}>
          {s >= 60 ? `${Math.floor(s / 60)}m${s % 60 ? ` ${s % 60}s` : ""}` : `${s}s`}
        </option>
      ))}
    </select>
  )
}

function Switch({
  on,
  label,
  onChange,
}: {
  on: boolean
  label: string
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={cx(
        // 24px pill with an invisible ~44px hit area (matches the marketing
        // MasterToggle).
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50",
        "before:absolute before:-inset-2.5 before:content-['']",
        on ? "bg-emerald-500" : "bg-zinc-300",
      )}
    >
      <span
        className={cx(
          "inline-block size-5 rounded-full bg-white shadow transition-transform",
          on ? "translate-x-[22px]" : "translate-x-0.5",
        )}
      />
    </button>
  )
}
